import React, { useEffect, useState } from 'react';
import {
  Container, Card, CardBody, Row, Col, Button, Spinner,
  FormGroup, Label, Input, Offcanvas, OffcanvasHeader,
  OffcanvasBody, Badge, FormText
} from 'reactstrap';
import Select from 'react-select';
import { createSelectStyles } from 'helpers/reactSelectLayout';
import { useSelector, useDispatch } from 'react-redux';
import { tenantUpdateRequest } from 'store/user/action';
import { userGetRequest } from 'store/auth/action';
import { studentsMetadataGetRequest } from 'store/student/action';
import { v4 as uuidv4 } from 'uuid';
import type { ApplicationState } from 'store';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { Action } from 'redux';

type Category = {
  id: string;
  label: string;
  fields: string[];
};
type SkillSettings = {
  categories: Category[];
};

export default function AdminSkillsSettings() {
  const dispatch = useDispatch<ThunkDispatch<any, null, Action<string>>>();
  const tenant = useSelector((s: ApplicationState) => s.auth.userProfile.tenant);
  const meta = useSelector((s: ApplicationState) => s.student.metaDataFields);
  const selectStyles = createSelectStyles('light');

  useEffect(() => {
    dispatch(studentsMetadataGetRequest('students', { pageSize: 500 }));
  }, [dispatch]);

  const fieldOptions = (meta || [])
    .map((f) => {
      const js = typeof f.valuesJson === 'string'
        ? JSON.parse(f.valuesJson)
        : f.valuesJson;
      const key = js?.key;
      const label = js?.label || key;
      const options = js?.values;

      const hasValidOptions =
        Array.isArray(options) &&
        options.some((opt) => opt?.value && opt?.label);

      if (!hasValidOptions) return null;

      return { value: key, label };
    })
    .filter(Boolean);

  const buildDefault = (): SkillSettings => ({ categories: [] });
  const parseRaw = (raw: string | null): SkillSettings => {
    if (!raw) return buildDefault();
    try {
      return JSON.parse(raw);
    } catch {
      return buildDefault();
    }
  };

  const [cfg, setCfg] = useState<SkillSettings>(buildDefault());
  const [loadingCfg, setLoadingCfg] = useState(true);

  const [canvasOpen, setCanvas] = useState(false);
  const [editCat, setEditCat] = useState<Category | null>(null);
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    setCfg(parseRaw(tenant?.skillSettings ?? null));
    setLoadingCfg(false);
  }, [tenant]);

  const openNew = () => {
    setEditCat({ id: uuidv4(), label: '', fields: [] });
    setCanvas(true);
  };

  const openEdit = (cat: Category) => {
    setEditCat({ ...cat });
    setCanvas(true);
  };

  const removeCat = (id: string) => {
    const next = { categories: cfg.categories.filter((x) => x.id !== id) };
    setCfg(next);
    dispatch(tenantUpdateRequest({
      attributes: [{ key: 'skill_settings', value: JSON.stringify(next) }]
    }, () => dispatch(userGetRequest()), () => {}));
  };

  const saveCat = () => {
    if (!editCat || !editCat.label.trim()) return;

    // Prevent saving invalid fields
    const allValid = editCat.fields.every((f) =>
      !!fieldOptions.find((opt) => opt?.value === f)
    );    
    if (!allValid) {
      alert('One or more selected fields are invalid. Please recheck.');
      return;
    }

    setSavingCat(true);
    const exists = cfg.categories.some((x) => x.id === editCat.id);
    const nextCats = exists
      ? cfg.categories.map((x) => x.id === editCat.id ? editCat : x)
      : [...cfg.categories, editCat];
    const next = { categories: nextCats };
    setCfg(next);

    dispatch(tenantUpdateRequest({
      attributes: [{ key: 'skill_settings', value: JSON.stringify(next) }]
    }, () => {
      dispatch(userGetRequest());
      setSavingCat(false);
      setCanvas(false);
    }, () => {
      setSavingCat(false);
    }));
  };

  if (!meta || loadingCfg) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner size="lg" /> Loading…
      </Container>
    );
  }

  return (
    <Container fluid>
      <Card className="border-0 shadow-sm">
        <CardBody className="pb-4">
          <div className="d-flex justify-content-between mb-4">
            <div>
              <h4 className="fw-semibold">
                <i className="ri-brain-line me-2" />
                Skill Categories
              </h4>
              <p className="text-muted small mb-0">
                Define categories and map them to student fields for skill-based auto assignment.
              </p>
            </div>
            <Button color="primary" onClick={openNew}>
              <i className="ri-add-line me-1" /> New Category
            </Button>
          </div>

          {cfg.categories.length === 0 ? (
            <div className="text-muted text-center py-5">
              <p>No categories defined yet.</p>
              <p>Click <strong>New Category</strong> to begin mapping.</p>
            </div>
          ) : (
            <Row xs={1} sm={2} md={3} className="g-3">
              {cfg.categories.map((cat) => (
                <Col key={cat.id}>
                  <div className="border rounded bg-light-subtle p-3 h-100 d-flex flex-column justify-content-between">
                    <div>
                      <h6 className="fw-semibold mb-2">{cat.label}</h6>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {cat.fields.length > 0 ? (
                          cat.fields.map((f) => {
                            const opt = fieldOptions.find((o) => o?.value === f);
                            return (
                              <span key={f} className="badge bg-primary-subtle text-primary fw-normal rounded-pill px-3 py-1 small">
                                {opt?.label || f}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-muted small">No fields mapped</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2 justify-content-end">
                      <Button color="secondary" size="sm" onClick={() => openEdit(cat)}>
                        <i className="ri-pencil-line me-1" /> Edit
                      </Button>
                      <Button color="danger" size="sm" onClick={() => removeCat(cat.id)}>
                        <i className="ri-delete-bin-line me-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </CardBody>
      </Card>

      <Offcanvas isOpen={canvasOpen} toggle={() => setCanvas(false)} direction="end" size="lg">
        <OffcanvasHeader toggle={() => setCanvas(false)}>
          {editCat && cfg.categories.some((x) => x.id === editCat.id)
            ? 'Edit Category'
            : 'New Category'}
        </OffcanvasHeader>
        <OffcanvasBody className="pt-3">
          <FormGroup className="mb-4">
            <Label className="fw-semibold">Category Name</Label>
            <Input
              type="text"
              value={editCat?.label || ''}
              onChange={(e) =>
                setEditCat((c) => c && { ...c, label: e.target.value })
              }
              placeholder="e.g. Language Expertise"
            />
            <FormText color="muted">
              This is the name shown when matching records to user skills.
            </FormText>
          </FormGroup>

          <FormGroup className="mb-4">
            <Label className="fw-semibold">Mapped Metadata Fields</Label>
            <Select
              isMulti
              styles={selectStyles}
              options={fieldOptions}
              value={fieldOptions.filter((o) =>
                editCat?.fields?.includes(o?.value || '')
              )}              
              onChange={(vals: any[]) =>
                setEditCat((c) =>
                  c && { ...c, fields: vals.map((v) => v.value) }
                )
              }
              placeholder="Select one or more fields…"
            />
            <FormText color="muted">
              Only dropdown fields with fixed value lists can be used for skills.
            </FormText>
          </FormGroup>

          <div className="text-end mt-4">
            <Button
              color="primary"
              onClick={saveCat}
              disabled={savingCat || !editCat?.label.trim()}
            >
              {savingCat ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                'Save Category'
              )}
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
}
